import { Inject, Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { STORAGE_KEYS } from '../../declarations/storage-keys.enum';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { logout, setCurrentUser, setUsersFilter } from './user.actions';
import { DOCUMENT } from '@angular/common';
import {
  SubscribeToBENotifications,
  UnsubscribeFromBENotifications
} from '../message-notifications-state/message-notifications.actions';
import { setUser } from '@sentry/angular';
import { TokenService } from '../../../shared/services/token.service';
import { SET_CURRENT_TENANT } from '../tenant-state/tenant.actions';
import { getCurrentTenantSettings } from '../tenant-state/tenant.selectors';
import { setCurrentUserSetting } from '../user-setting-state/user-setting.actions';

@Injectable()
export class UserEffects {
  logout$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      tap(async () => {
        this.tokenService.clear();
        await Preferences.remove({ key: STORAGE_KEYS.AUTH });
        setUser(null);
      }),
      map(() => UnsubscribeFromBENotifications())
    )
  );

  setCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setCurrentUser),
      map(() => SubscribeToBENotifications())
    )
  );

  setHighContrast$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setCurrentUser),
        filter(u => u != null),
        tap(user => {
          const style = this.document.documentElement.style;
          if (user.user_setting?.high_contrast) {
            style.setProperty(
              '--ion-color-input-border',
              'var(--ion-color-medium)'
            );
            style.setProperty(
              '--segment-button-color',
              'var(--ion-color-white)'
            );
            style.setProperty(
              '--poll-option-background',
              'var(--ion-color-white)'
            );
          } else {
            style.setProperty(
              '--ion-color-input-border',
              'var(--ion-color-pattensblue)'
            );
            style.removeProperty('--segment-button-color');
            style.removeProperty('--poll-option-background');
          }
        })
      ),
    { dispatch: false }
  );

  setSentryUser$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setCurrentUser),
        filter(u => u != null),
        tap(({ id }) => {
          setUser({
            id: String(id)
          });
        })
      ),
    { dispatch: false }
  );

  updateCurrentUserSetting$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setCurrentUser),
        filter(user => user != null),
        tap(user => {
          this.store.dispatch(setCurrentUserSetting(user.user_setting));
        })
      ),
    { dispatch: false }
  );

  initializeUserFiltersAfterTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_CURRENT_TENANT),
      withLatestFrom(this.store.select(getCurrentTenantSettings)),
      map(([_, tenantSettings]) => {
        return setUsersFilter({
          origin: tenantSettings?.filter_by_location_by_default
            ? 'my_settlement'
            : 'all'
        });
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private tokenService: TokenService,
    @Inject(DOCUMENT) private document: Document
  ) {}
}
